'use client';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Send, X, Paperclip, Smile } from 'lucide-react';
import api from '@/config/api';
import { messageAPI } from '@/config/api';
import { showSuccessNotification, showErrorNotification } from '@/utils/notificationService';
import { validateMessage } from '@/utils/bannedWords';

const MessageModal = ({ isOpen, onClose, freelancerData }) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [prefilledMessages, setPrefilledMessages] = useState([]);
  const [loadingPrefilled, setLoadingPrefilled] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [mounted, setMounted] = useState(false);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const lastMessageIdRef = useRef(null);
  
  // Get authentication data from Redux store
  const { user, token, isAuthenticated } = useSelector((state) => state.auth);
  const currentUserId = user?.id;

  // Fetch prefilled messages from API
  const fetchPrefilledMessages = async () => {
    setLoadingPrefilled(true);
    try {
      const response = await messageAPI.getPrefilledMessages();
      if (response.status === 'success' && response.messages) {
        // Handle the actual API response structure
        // The API returns messages with createdBy relationship
        const messages = response.messages.map(msg => ({
          id: msg.id,
          text: msg.body || msg.message || msg.text || msg.content, // Handle different possible field names
          category: msg.created_by?.ad_name || 'General',
          createdBy: msg.created_by
        }));
        setPrefilledMessages(messages);
      } else {
        console.warn('Failed to fetch prefilled messages:', response);
        // Fallback to default messages if API fails
        setPrefilledMessages(getDefaultPrefilledMessages());
      }
    } catch (error) {
      console.error('Error fetching prefilled messages:', error);
      // Fallback to default messages if API fails
      setPrefilledMessages(getDefaultPrefilledMessages());
    } finally {
      setLoadingPrefilled(false);
    }
  };

  // Default prefilled messages as fallback
  const getDefaultPrefilledMessages = () => [
    {
      id: 1,
      text: "Hi! I'm interested in your services. Can we discuss my project?",
      category: "General"
    },
    {
      id: 2,
      text: "Hello! I'd like to know more about your availability and rates.",
      category: "General"
    },
    {
      id: 3,
      text: "Hi there! I have a project that might be a good fit for your skills.",
      category: "General"
    },
    {
      id: 4,
      text: "Hello! Could you please share more details about your experience?",
      category: "General"
    },
    {
      id: 5,
      text: "Hi! I'm looking for someone with your expertise. Are you available?",
      category: "General"
    }
  ];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && freelancerData) {
      // Reset flags when modal opens
      isInitialLoadRef.current = true;
      lastMessageIdRef.current = null;
      
      // Load conversation history
      loadMessages(true);
      // Fetch prefilled messages
      fetchPrefilledMessages();
      
      // Setup auto-refresh interval to check for new messages
      refreshIntervalRef.current = setInterval(() => {
        loadMessages(false);
      }, 5000); // Check for new messages every 5 seconds
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isOpen, freelancerData]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clean up when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setMessage('');
      setMessageError('');
      isInitialLoadRef.current = true;
      lastMessageIdRef.current = null;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async (isInitialLoad = false) => {
    if (!freelancerData?.fp_u_id || !token) return;
    
    try {
      // Only show loading indicator on initial load
      if (isInitialLoad) {
        setIsLoading(true);
        isInitialLoadRef.current = false;
      }
      
      const response = await api.get(`/conversation/${freelancerData.fp_u_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const data = response.data;
      
      if (data.status && data.data && Array.isArray(data.data)) {
        const formattedMessages = data.data.map(msg => ({
          id: msg.uc_id,
          text: msg.uc_message,
          sender: msg.uc_sender_id === currentUserId ? 'me' : 'other',
          timestamp: formatMessageTime(msg.created_at),
          rawTimestamp: msg.created_at,
          isRead: msg.is_read
        }));
        
        // Sort messages by creation time (ascending)
        formattedMessages.sort((a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp));
        
        // Check if there are actually new messages by comparing the latest message ID
        const latestMessageId = formattedMessages.length > 0 
          ? formattedMessages[formattedMessages.length - 1].id 
          : null;
        
        // Only update state if messages have actually changed (new message or initial load)
        // This prevents unnecessary re-renders during auto-refresh when no new messages exist
        if (isInitialLoad || latestMessageId !== lastMessageIdRef.current) {
          lastMessageIdRef.current = latestMessageId;
          setMessages(formattedMessages);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      // Only show error on initial load, not on refresh
      if (isInitialLoad) {
        setIsLoading(false);
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || !freelancerData?.fp_u_id || !token) return;
    
    const receiverId = freelancerData.fp_u_id;
    
    // Optimistically add message to UI immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      text: messageText,
      sender: 'me',
      timestamp: formatMessageTime(new Date()),
      rawTimestamp: new Date().toISOString(),
      isRead: 0,
      sending: true
    };

    setMessages(prev => [...prev, tempMessage]);
    
    try {
      const response = await api.post('/send', {
        receiver_id: receiverId,
        message: messageText
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      const data = response.data;
      
      if (data.status && data.data) {
        // Replace optimistic message with real message from server
        const realMessage = {
          id: data.data.uc_id,
          text: messageText,
          sender: 'me',
          timestamp: formatMessageTime(data.data.created_at),
          rawTimestamp: data.data.created_at,
          isRead: data.data.is_read,
          sending: false
        };

        setMessages(prev => 
          prev.map(msg => msg.id === tempMessage.id ? realMessage : msg)
        );
        
        // Update last message ID to prevent unnecessary refresh
        lastMessageIdRef.current = data.data.uc_id;
        
        showSuccessNotification('Message sent successfully!');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      showErrorNotification('Failed to send message. Please try again.');
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    const messageText = message.trim();
    
    // Check for banned words
    const validationError = validateMessage(messageText);
    if (validationError) {
      setMessageError(validationError);
      showErrorNotification(validationError);
      return;
    }
    
    setMessage('');
    setMessageError('');
    await sendMessage(messageText);
  };

  const handleMessageChange = (e) => {
    const newMessage = e.target.value;
    setMessage(newMessage);
    
    // Real-time validation (only show error if user has typed something)
    if (newMessage.trim()) {
      const validationError = validateMessage(newMessage.trim());
      setMessageError(validationError || '');
    } else {
      setMessageError('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!messageError) {
        handleSendMessage();
      }
    }
  };

  const handlePrefilledMessage = (prefilledText) => {
    // Check for banned words in prefilled message
    const validationError = validateMessage(prefilledText);
    if (validationError) {
      showErrorNotification('This prefilled message contains inappropriate content.');
      return;
    }
    setMessage(prefilledText);
  };

  if (!isOpen || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[600px] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-gray-600 font-medium text-sm">
                {freelancerData?.fp_display_name?.charAt(0) || 'F'}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {freelancerData?.fp_display_name || 'Freelancer'}
              </h3>
              <p className="text-sm text-gray-500">Online</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading && messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3e5a9a] mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">Loading conversation...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">Start a conversation with {freelancerData?.fp_display_name || 'this freelancer'}</p>
              <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-3">Quick messages:</p>
                {loadingPrefilled ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3e5a9a]"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading messages...</span>
                  </div>
                ) : (
                  prefilledMessages.map((msg, index) => (
                    <button
                      key={msg.id || index}
                      onClick={() => handlePrefilledMessage(msg.text)}
                      className="block w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      {msg.text}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${msg.sender === 'me' ? 'ml-auto' : 'mr-auto'}`}>
                    <div
                      className={`px-4 py-2 rounded-2xl shadow-sm relative ${
                        msg.sender === 'me'
                          ? 'bg-[#3e5a9a] text-white rounded-br-md'
                          : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                      }`}
                    >
                      <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                      <div className={`flex items-center justify-between mt-1 ${
                        msg.sender === 'me' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        <span className="text-xs">
                          {msg.timestamp}
                        </span>
                        {msg.sending && (
                          <div className="ml-2">
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="p-6 border-t border-gray-100">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={message}
                onChange={handleMessageChange}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent ${
                  messageError 
                    ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                    : 'border-gray-200 focus:ring-[#3e5a9a]'
                }`}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !message.trim() || !!messageError}
                className="bg-[#3e5a9a] text-white p-3 rounded-xl hover:bg-[#2d4a7a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
            {messageError && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-700 flex-1">{messageError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MessageModal;
